import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Building2, Loader2, MapPin, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY_BIZ = {
  business_name: "", contact_person: "", business_email: "", phone: "", gstin: "",
  billing: { line1: "", line2: "", city: "", state: "", pin: "" },
};
const EMPTY_ADDR = { label: "", full_name: "", mobile: "", line1: "", line2: "", city: "", state: "", pin: "", is_default: false };

function Field({ label, hint, ...props }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-3">{label}</span>
      <input {...props}
        className="h-11 w-full rounded-xl border border-line bg-hover px-3.5 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none" />
      {hint && <span className="mt-1 block text-[11px] text-3">{hint}</span>}
    </label>
  );
}

function Card({ title, icon: Icon, children, action }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-6 shadow-card">
      <div className="mb-5 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-2">
          {Icon && <Icon className="h-4 w-4 text-amber-400" />} {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function AddressForm({ initial, onSave, onCancel, busy }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="address-form">
      <Field label="Label" placeholder="Office, Warehouse…" value={f.label} onChange={set("label")} maxLength={30} data-testid="addr-label" />
      <Field label="Receiver name" value={f.full_name} onChange={set("full_name")} maxLength={80} data-testid="addr-full-name" />
      <Field label="Mobile" value={f.mobile} onChange={set("mobile")} maxLength={15} data-testid="addr-mobile" />
      <Field label="Address line 1" value={f.line1} onChange={set("line1")} maxLength={120} data-testid="addr-line1" />
      <Field label="Address line 2 (optional)" value={f.line2} onChange={set("line2")} maxLength={120} data-testid="addr-line2" />
      <Field label="City" value={f.city} onChange={set("city")} maxLength={60} data-testid="addr-city" />
      <Field label="State" value={f.state} onChange={set("state")} maxLength={60} data-testid="addr-state" />
      <Field label="PIN code" value={f.pin} onChange={set("pin")} maxLength={10} data-testid="addr-pin" />
      <div className="flex items-end gap-2 sm:col-span-2">
        <button onClick={() => onSave(f)} disabled={busy} data-testid="addr-save"
          className="flex h-11 items-center gap-2 rounded-full bg-amber-500 px-6 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-40">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save address
        </button>
        <button onClick={onCancel} data-testid="addr-cancel"
          className="h-11 rounded-full border border-strong px-5 text-sm font-semibold text-2 hoverable-t1">Cancel</button>
      </div>
    </div>
  );
}

export default function BusinessProfilePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(EMPTY_BIZ);
  const [saving, setSaving] = useState(false);
  const [addrForm, setAddrForm] = useState(null); // null | "new" | addr id
  const [addrBusy, setAddrBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // addr id awaiting confirm

  useEffect(() => {
    if (user?.business) {
      const b = user.business;
      setBiz({
        business_name: b.business_name || "",
        contact_person: b.contact_person || "",
        business_email: b.business_email || "",
        phone: b.phone || "",
        gstin: b.gstin || "",
        billing: { ...EMPTY_BIZ.billing, ...(b.billing_address || {}) },
      });
    }
  }, [user]);

  if (user === undefined) {
    return (
      <div className="mx-auto max-w-4xl animate-pulse px-6 pb-24 pt-28" data-testid="business-profile-loading">
        <div className="h-3 w-20 rounded bg-hover" />
        <div className="mt-3 h-9 w-64 rounded-lg bg-hover" />
        <div className="mt-2 h-4 w-80 rounded bg-hover" />
        <div className="mt-10 space-y-6">
          {[1, 2].map((i) => (
            <div key={i} className="rounded-2xl border border-line bg-surface p-6 shadow-card">
              <div className="h-4 w-40 rounded bg-hover" />
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="space-y-2">
                    <div className="h-3 w-24 rounded bg-hover" />
                    <div className="h-11 w-full rounded-xl bg-hover" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <button onClick={() => navigate("/login", { state: { from: "/account/business" } })} data-testid="bp-login-cta"
          className="rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-black">Sign in to manage your business profile</button>
      </div>
    );
  }

  const addresses = user.addresses || [];
  const setB = (k) => (e) => setBiz({ ...biz, [k]: e.target.value });
  const setBill = (k) => (e) => setBiz({ ...biz, billing: { ...biz.billing, [k]: e.target.value } });

  const saveBusiness = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const hasBilling = Object.values(biz.billing).some((v) => v.trim());
      await api.put("/auth/me/business", {
        business_name: biz.business_name.trim(),
        contact_person: biz.contact_person.trim(),
        business_email: biz.business_email.trim(),
        phone: biz.phone.trim(),
        gstin: biz.gstin.trim(),
        billing_address: hasBilling ? biz.billing : null,
      });
      await refreshUser();
      toast.success("Business profile saved");
    } catch (e) {
      toast.error(apiError(e, "Couldn't save your business profile"));
    } finally {
      setSaving(false);
    }
  };

  const saveAddress = async (f) => {
    if (addrBusy) return;
    setAddrBusy(true);
    try {
      const payload = { ...f, label: f.label.trim() || "Address", country: "India" };
      if (addrForm === "new") await api.post("/auth/me/addresses", payload);
      else await api.put(`/auth/me/addresses/${addrForm}`, payload);
      await refreshUser();
      setAddrForm(null);
      toast.success("Address saved");
    } catch (e) {
      toast.error(apiError(e, "Couldn't save the address"));
    } finally {
      setAddrBusy(false);
    }
  };

  const setDefault = async (id) => {
    try {
      await api.post(`/auth/me/addresses/${id}/default`);
      await refreshUser();
      toast.success("Default delivery address updated");
    } catch (e) { toast.error(apiError(e, "Couldn't update the default")); }
  };

  const removeAddress = async (id) => {
    if (confirmDelete !== id) {
      setConfirmDelete(id);
      setTimeout(() => setConfirmDelete((c) => (c === id ? null : c)), 3000);
      return;
    }
    setConfirmDelete(null);
    try {
      await api.delete(`/auth/me/addresses/${id}`);
      await refreshUser();
      toast.success("Address removed");
    } catch (e) { toast.error(apiError(e, "Couldn't remove the address")); }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 pb-24 pt-28" data-testid="business-profile-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
          <span className="h-px w-10 bg-amber-500" /> Account
        </p>
        <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-1 sm:text-4xl">Business Profile</h1>
        <p className="mt-2 text-sm text-3">Used to prefill checkout and keep your delivery addresses handy.</p>
      </motion.div>

      <div className="mt-10 space-y-6">
        <Card title="Business details" icon={Building2}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Business / company name" value={biz.business_name} onChange={setB("business_name")} maxLength={120} data-testid="bp-business-name" />
            <Field label="Contact person" value={biz.contact_person} onChange={setB("contact_person")} maxLength={80} data-testid="bp-contact-person" />
            <Field label="Business email" type="email" value={biz.business_email} onChange={setB("business_email")} maxLength={120} data-testid="bp-business-email" />
            <Field label="Phone number" value={biz.phone} onChange={setB("phone")} maxLength={15} data-testid="bp-phone" />
            <Field label="GSTIN" value={biz.gstin} onChange={setB("gstin")} maxLength={15}
              hint="Optional — not required while PAQVERO is Pre-GST." data-testid="bp-gstin" />
          </div>
          <div className="mt-6 border-t border-line pt-5">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-3">Billing address (optional)</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Address line 1" value={biz.billing.line1} onChange={setBill("line1")} maxLength={120} data-testid="bp-bill-line1" />
              <Field label="Address line 2" value={biz.billing.line2} onChange={setBill("line2")} maxLength={120} data-testid="bp-bill-line2" />
              <Field label="City" value={biz.billing.city} onChange={setBill("city")} maxLength={60} data-testid="bp-bill-city" />
              <Field label="State" value={biz.billing.state} onChange={setBill("state")} maxLength={60} data-testid="bp-bill-state" />
              <Field label="PIN code" value={biz.billing.pin} onChange={setBill("pin")} maxLength={10} data-testid="bp-bill-pin" />
            </div>
          </div>
          <button onClick={saveBusiness} disabled={saving} data-testid="bp-save"
            className="mt-6 flex h-12 items-center gap-2 rounded-full bg-amber-500 px-8 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-40">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save business profile
          </button>
        </Card>

        <Card title="Delivery addresses" icon={MapPin} action={
          addrForm === null && (
            <button onClick={() => setAddrForm("new")} data-testid="address-add-btn"
              className="flex h-9 items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-4 text-xs font-bold text-amber-300 transition-colors hover:bg-amber-500/20">
              <Plus className="h-3.5 w-3.5" /> Add address
            </button>
          )
        }>
          {addrForm === "new" && (
            <div className="mb-5 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4">
              <AddressForm initial={EMPTY_ADDR} onSave={saveAddress} onCancel={() => setAddrForm(null)} busy={addrBusy} />
            </div>
          )}
          {addresses.length === 0 && addrForm !== "new" ? (
            <p className="text-sm text-3" data-testid="addresses-empty">No delivery addresses saved yet — add one and checkout gets faster.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {addresses.map((a) => (
                <div key={a.id} data-testid={`address-card-${a.id}`}
                  className={`rounded-xl border p-4 ${a.is_default ? "border-amber-500/40 bg-amber-500/[0.05]" : "border-line bg-hover"}`}>
                  {addrForm === a.id ? (
                    <AddressForm initial={{ ...EMPTY_ADDR, ...a }} onSave={saveAddress} onCancel={() => setAddrForm(null)} busy={addrBusy} />
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-bold text-1">{a.label || "Address"}</p>
                        {a.is_default && (
                          <span className="flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300"
                            data-testid={`address-default-badge-${a.id}`}>
                            <Star className="h-3 w-3 fill-current" /> Default
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 text-sm text-2">{a.full_name} · {a.mobile}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-3">
                        {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pin}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {!a.is_default && (
                          <button onClick={() => setDefault(a.id)} data-testid={`address-set-default-${a.id}`}
                            className="h-8 rounded-full border border-strong px-3 text-[11px] font-semibold text-2 transition-colors hover:border-amber-500/40 hover:text-amber-300">
                            Set as default
                          </button>
                        )}
                        <button onClick={() => setAddrForm(a.id)} data-testid={`address-edit-${a.id}`}
                          className="flex h-8 items-center gap-1 rounded-full border border-strong px-3 text-[11px] font-semibold text-2 transition-colors hoverable-t1">
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                        <button onClick={() => removeAddress(a.id)} data-testid={`address-delete-${a.id}`}
                          className={`flex h-8 items-center gap-1 rounded-full border px-3 text-[11px] font-semibold transition-colors ${
                            confirmDelete === a.id
                              ? "border-red-500/50 bg-red-500/10 text-red-300"
                              : "border-strong text-2 hover:border-red-500/40 hover:text-red-300"
                          }`}>
                          <Trash2 className="h-3 w-3" /> {confirmDelete === a.id ? "Tap again to delete" : "Delete"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
